import type { ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { ActionMenu, HelpPopover, cx, type ActionMenuGroup, type ActionMenuItem } from "@wato/ui";

/**
 * The «كيف تعمل؟» help slot: collapsed it is a small chip beside the title; expanded it drops onto
 * its own row and renders in flow, as a card inside the content column (never floating/overlapping).
 */
export const HELP_SLOT_CLASS = cx(
  "has-[[data-feature-intro][data-state=expanded]]:basis-full",
  "[&_[data-feature-intro][data-state=expanded]]:w-full [&_[data-feature-intro][data-state=expanded]]:max-w-full",
  "[&_[data-feature-intro][data-state=expanded]]:bg-surface [&_[data-feature-intro][data-state=expanded]]:shadow-sm",
);

/**
 * UX-11: the one compact page header band — title with the help chip beside it, one-line
 * description under it, secondary links + the primary action at the end side (flips in RTL).
 * `extra` sits beside the title (e.g. a video-help link); `help` is the «كيف تعمل؟» intro.
 */
export function PageHeader({
  title,
  description,
  action,
  extra,
  help,
  secondary,
  primary,
  menu,
  menuLabel,
  helpContent,
}: {
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
  readonly extra?: ReactNode;
  readonly help?: ReactNode;
  readonly secondary?: ReactNode;
  /** v2 (OWN-173 G1): the page's single primary action (a Button or SplitButton). */
  readonly primary?: ReactNode;
  /** v2: secondary actions as a `⋯` menu next to the primary. */
  readonly menu?: readonly ActionMenuItem[] | readonly ActionMenuGroup[];
  readonly menuLabel?: string;
  /** v2: help text shown in a `?` popover beside the title (sheet on phones). */
  readonly helpContent?: ReactNode;
}) {
  const { t } = useI18n();
  const hasMenu = menu !== undefined && menu.length > 0;
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2" data-page-header>
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-2xl font-bold text-fg">{title}</h2>
          {extra}
          {help ? <div className={HELP_SLOT_CLASS}>{help}</div> : null}
          {helpContent ? (
            <HelpPopover>{helpContent}</HelpPopover>
          ) : description && !help ? (
            <HelpPopover>{description}</HelpPopover>
          ) : null}
        </div>
        {description ? <p className="sr-only">{description}</p> : null}
      </div>
      {secondary || action || primary || hasMenu ? (
        <div className="flex flex-wrap items-center gap-2">
          {secondary}
          {action}
          {hasMenu ? (
            <ActionMenu
              label={menuLabel ?? t("ui.menu.more")}
              items={menu}
              testId="page-header-menu"
            />
          ) : null}
          {primary}
        </div>
      ) : null}
    </div>
  );
}
